import { defineStore } from 'pinia';

import { type ChatBotMessage } from '@/features/Eli/types';
import {
	type ELI_CHAT_PROJECT_HEALTH_CONTEXT,
	type ELI_CHAT_GENERAL_CHAT_CONTEXT,
	ELI_CHAT_IS_SIDEBAR_KEY,
	ELI_CHAT_THREAD_ID_KEY,
	ELI_CHAT_TITLE_KEY,
	ELI_CHAT_MESSAGES_KEY,
	ELI_OPEN_CHAT_TYPE_KEY,
	ELI_CHAT_IS_MINIMIZED_KEY,
	ELI_CHAT_CONTEXT_KEY,
	ELI_CHAT_IS_SIDEBAR_PREFERENCE_KEY,
	ELI_CHAT_POSITION_KEY,
	ELI_CHAT_DIMENSION_KEY,
} from '@/features/Eli/components/EliChat/EliChatGeneral/constants';
import { useLocalStorage, useSessionStorage } from '@vueuse/core';
import { type Ref } from 'vue';
import type { EliChatMessageResponse } from '@/generated/graphql';
import { type EliContextDataInput } from '@/features/Eli/components/EliChat/types';
import { getCookie } from '@/utils/cookies';

export type EliChatStore = {
	messages: Ref<ChatBotMessage[]>;
	threadId: Ref<EliChatMessageResponse['threadId'] | null>;
	chatTitle: Ref<string>;
	shouldHaveSidebar: Ref<boolean>;
	isMinimized: Ref<boolean>;
	eliContext: Ref<EliContextDataInput | null>;
	isSidebar: Ref<boolean>;
	isTyping: boolean;
	isThinking: boolean;
	isFinishingUp: boolean;
	chatPositionStorage: Ref<string>;
	chatDimensionStorage: Ref<string>;
	openChatType: Ref<
		| typeof ELI_CHAT_PROJECT_HEALTH_CONTEXT
		| typeof ELI_CHAT_GENERAL_CHAT_CONTEXT
		| undefined
	>;
	scrollToEnd: (forceScroll?: boolean) => Promise<void>;
	getInput: () => HTMLTextAreaElement | HTMLInputElement | undefined;
	showChatRatingDelayPassed: boolean;
	maxContentLengthForAutoScroll: number | null;
};

const welcomeThreadId = getCookie('eliWelcomeThreadId');

export const useELiChatStore = defineStore('generalEliChat', {
	state: (): EliChatStore => {
		return {
			messages: useSessionStorage(
				ELI_CHAT_MESSAGES_KEY,
				[] as ChatBotMessage[]
			),
			threadId: useSessionStorage(
				ELI_CHAT_THREAD_ID_KEY,
				welcomeThreadId ?? null
			),
			chatTitle: useSessionStorage(ELI_CHAT_TITLE_KEY, ''),
			shouldHaveSidebar: useLocalStorage(
				ELI_CHAT_IS_SIDEBAR_PREFERENCE_KEY,
				false
			),
			openChatType: useSessionStorage(ELI_OPEN_CHAT_TYPE_KEY, undefined),
			isMinimized: useSessionStorage(ELI_CHAT_IS_MINIMIZED_KEY, false),
			eliContext: useSessionStorage(ELI_CHAT_CONTEXT_KEY, null),
			isSidebar: useLocalStorage(ELI_CHAT_IS_SIDEBAR_KEY, false),
			isTyping: false,
			isThinking: false,
			isFinishingUp: false,
			chatPositionStorage: useLocalStorage(ELI_CHAT_POSITION_KEY, ''),
			chatDimensionStorage: useLocalStorage(ELI_CHAT_DIMENSION_KEY, ''),
			scrollToEnd: () => Promise.resolve(),
			getInput: () => undefined,
			showChatRatingDelayPassed: false,
			maxContentLengthForAutoScroll: 200,
		};
	},
});
